/**
 * Maps a cell registration status to its human-readable form
 */
import { isSameDay } from "date-fns";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { MapListDisplayType } from "../components/dashboard/dropdown/map-list/map-list-display-type";
import {
	AnyVelavuEvent,
	AssetCategory,
	DeviceCategory,
	ReleaseChannel,
	DFURequestStatus,
	EventCategory,
	EventCategoryMap,
	LocationType,
	NormalizedDeviceHardware,
	VelavuAlert,
	VelavuEvent,
	VelavuFloor,
	VelavuRuleCategory,
	VelavuSite,
	DeviceIndicatorType,
	VelavuInvoiceStatus,
	VelavuRole,
	VelavuUserStatus,
} from "velavu-js-api";
import AnalyticsStatType from "../data/analytics-stat-type";
import ConnectionStatus from "../data/connection-status";
import { DateRange } from "../data/date-range";
import FloorFilter from "../data/floor-filter";
import { EventGroup } from "../data/event-group";

export function mapCellRegistrationStatus(
	value: CellStatus | undefined,
	t: TFunction,
) {
	switch (value) {
		case "REGISTERED_HOME":
			return t("cell_status.registered_home");
		case "REGISTERED_ROAMING":
			return t("cell_status.registered_roaming");
		default:
			return value ?? t("cell_status.unknown");
	}
}

/**
 * Maps an internal user registration type to its human-readable form
 */
export function mapUserRegistrationStatus(type: string, t: TFunction) {
	switch (type) {
		case VelavuUserStatus.ForcePasswordChange:
			return t("user_status.pending");
		case VelavuUserStatus.Confirmed:
			return t("user_status.active");
		default:
			return type;
	}
}

/**
 * Maps a user role to its human-readable form
 */
export function mapUserRole(role: VelavuRole | string, t: TFunction) {
	switch (role) {
		case VelavuRole.Viewer:
			return t("settings.organization.role_viewer");
		case VelavuRole.Operator:
			return t("settings.organization.role_operator");
		case VelavuRole.SiteManager:
			return t("settings.organization.role_site_manager");
		case VelavuRole.Admin:
			return t("settings.organization.role_admin");
		default:
			return role;
	}
}

/**
 * Builds human-readable strings from a Velavu route event
 * @param event The event to process
 * @return The title and subtitle strings to display in a tuple
 */
export function buildRouteEventStrings(
	event: VelavuEvent<EventCategory.DrivingEvent>,
	t: TFunction,
): [string, string] {
	let title: string;
	switch (event.data.direction) {
		case "ACCELERATION":
			title = t("events.route_events.harsh_acceleration");
			break;
		case "BRAKING":
			title = t("events.route_events.harsh_braking");
			break;
		case "TURNING":
			title = t("events.route_events.harsh_turning");
			break;
	}

	const subtitle = t("events.route_events.max_gforce", {
		gforce: event.data.max_g,
	});

	return [title, subtitle];
}

/**
 * Maps an event category to a human-readable short description
 */
export function mapEventCategoryDescription(
	category: EventCategory,
	t: TFunction,
): string {
	switch (category) {
		case EventCategory.BatteryLevel:
			return t("events.categories.battery_level");
		case EventCategory.DFURequest:
			return t("events.categories.dfu_request");
		case EventCategory.DeviceBoot:
			return t("events.categories.device_boot");
		case EventCategory.DeviceConnect:
			return t("events.categories.device_connect");
		case EventCategory.DeviceDisconnect:
			return t("events.categories.device_disconnect");
		case EventCategory.DeviceError:
			return t("events.categories.device_error");
		case EventCategory.DeviceProvision:
			return t("events.categories.device_provision");
		case EventCategory.DrivingEvent:
			return t("events.categories.driving_event");
		case EventCategory.DrivingRoute:
			return t("events.categories.driving_route");
		case EventCategory.Environment:
			return t("events.categories.environment");
		case EventCategory.EnvironmentThreshold:
			return t("events.categories.environment_threshold");
		case EventCategory.GeofenceEntered:
			return t("events.categories.geofence_entered");
		case EventCategory.GeofenceExited:
			return t("events.categories.geofence_exited");
		case EventCategory.Location:
			return t("events.categories.location");
		case EventCategory.LowBattery:
			return t("events.categories.low_battery");
		case EventCategory.Motion:
			return t("events.categories.motion");
		case EventCategory.OTAPRequest:
			return t("events.categories.otap_request");
		case EventCategory.Shock:
			return t("events.categories.shock");
		case EventCategory.SiteEntered:
			return t("events.categories.site_entered");
		case EventCategory.SiteExited:
			return t("events.categories.site_exited");
		case EventCategory.InventoryAdded:
			return t("events.categories.inventory_added");
		case EventCategory.InventoryRemoved:
			return t("events.categories.inventory_removed");
		case EventCategory.WearableAlert:
			return t("events.categories.wearable_alert");
		case EventCategory.CustomEvent:
			return t("events.categories.custom_event");
		default:
			return t("events.categories.unknown", { category });
	}
}

type NormalizedEvent = {
	[K in keyof EventCategoryMap]: {
		category: K;
		data: EventCategoryMap[K];
	};
}[keyof EventCategoryMap];

/**
 * Builds a human-readable summary string from a Velavu alert
 * @param item The event or alert to process
 * @return The summary string of the alert
 */
export function buildEventDescription(
	item: AnyVelavuEvent | VelavuAlert,
	t: TFunction,
): string {
	const event = {
		category:
			"event_category" in item ? item.event_category : item.category,
		data: item.data,
	} as NormalizedEvent;

	switch (event.category) {
		case EventCategory.DeviceConnect:
			return t("events.descriptions.reconnected");
		case EventCategory.DeviceDisconnect:
			return t("events.descriptions.disconnected");
		case EventCategory.DeviceProvision:
			return t("events.descriptions.registered");
		case EventCategory.DeviceBoot:
			return t("events.descriptions.powered_on");
		case EventCategory.LowBattery:
			return t("events.descriptions.battery_remaining", {
				level: event.data.battery_level,
			});
		case EventCategory.DrivingEvent: {
			switch (event.data.direction) {
				case "ACCELERATION":
					return t("events.descriptions.harsh_acceleration");
				case "BRAKING":
					return t("events.descriptions.harsh_braking");
				case "TURNING":
					return t("events.descriptions.harsh_turning");
			}
			break;
		}
		case EventCategory.Shock:
			return t("events.descriptions.shock", {
				gforce: event.data.gforce.toFixed(2),
			});
		case EventCategory.Motion:
			return t("events.descriptions.motion");
		case EventCategory.SiteEntered:
			return t("events.descriptions.site_entered", {
				site: event.data.site.name,
			});
		case EventCategory.SiteExited:
			return t("events.descriptions.site_exited", {
				site: event.data.site.name,
			});
		case EventCategory.GeofenceEntered:
			return t("events.descriptions.geofence_entered", {
				geofence: event.data.geofence.name,
			});
		case EventCategory.GeofenceExited:
			return t("events.descriptions.geofence_exited", {
				geofence: event.data.geofence.name,
			});
		case EventCategory.DeviceError:
			return t("events.descriptions.device_error", {
				level: event.data.level,
				type: event.data.type,
			});
		case EventCategory.InventoryAdded:
			return t("events.descriptions.inventory_added", {
				asset: event.data.inventory_asset.name,
			});
		case EventCategory.InventoryRemoved:
			return t("events.descriptions.inventory_removed", {
				asset: event.data.inventory_asset.name,
			});
		case EventCategory.WearableAlert:
			return t("events.descriptions.wearable_alert");
		case EventCategory.CustomEvent:
			return event.data.message;
		default:
			return t("events.descriptions.unknown_event", {
				category: event.category,
			});
	}
}

/**
 * Builds a human-readable summary string from a Velavu alert that includes
 * the asset or device that caused the event
 * @param item The event or alert to process
 * @return The summary string of the alert
 */
export function buildEventDescriptionWithOrigin(
	item: AnyVelavuEvent | VelavuAlert,
	t: TFunction,
): string {
	const event = {
		category:
			"event_category" in item ? item.event_category : item.category,
		data: item.data,
	} as NormalizedEvent;

	//Resolve the asset or device name
	let origin: string;
	if (
		item.data !== undefined &&
		"asset" in item.data &&
		item.data.asset?.name !== undefined
	) {
		origin = item.data.asset.name;
	} else if ("device" in item) {
		origin = item.device.id;
	} else {
		origin = item.device_id;
	}

	switch (event.category) {
		case EventCategory.DeviceConnect:
			return t("events.descriptions_with_origin.reconnected", { origin });
		case EventCategory.DeviceDisconnect:
			return t("events.descriptions_with_origin.disconnected", {
				origin,
			});
		case EventCategory.DeviceProvision:
			return t("events.descriptions_with_origin.registered", { origin });
		case EventCategory.DeviceBoot:
			return t("events.descriptions_with_origin.powered_on", { origin });
		case EventCategory.LowBattery:
			return t("events.descriptions_with_origin.battery_remaining", {
				origin,
				level: event.data.battery_level,
			});
		case EventCategory.DrivingEvent: {
			switch (event.data.direction) {
				case "ACCELERATION":
					return t(
						"events.descriptions_with_origin.harsh_acceleration",
						{ origin },
					);
				case "BRAKING":
					return t("events.descriptions_with_origin.harsh_braking", {
						origin,
					});
				case "TURNING":
					return t("events.descriptions_with_origin.harsh_turning", {
						origin,
					});
			}
			break;
		}
		case EventCategory.Shock:
			return t("events.descriptions_with_origin.shock", {
				origin,
				gforce: event.data.gforce.toFixed(2),
			});
		case EventCategory.Motion:
			return t("events.descriptions_with_origin.motion", { origin });
		case EventCategory.SiteEntered:
			return t("events.descriptions_with_origin.site_entered", {
				origin,
				site: event.data.site.name,
			});
		case EventCategory.SiteExited:
			return t("events.descriptions_with_origin.site_exited", {
				origin,
				site: event.data.site.name,
			});
		case EventCategory.GeofenceEntered:
			return t("events.descriptions_with_origin.geofence_entered", {
				origin,
				geofence: event.data.geofence.name,
			});
		case EventCategory.GeofenceExited:
			return t("events.descriptions_with_origin.geofence_exited", {
				origin,
				geofence: event.data.geofence.name,
			});
		case EventCategory.DeviceError:
			return t("events.descriptions_with_origin.device_error", {
				origin,
				level: event.data.level,
				type: event.data.type,
			});
		case EventCategory.InventoryAdded:
			return t("events.descriptions_with_origin.inventory_added", {
				origin,
				asset: event.data.inventory_asset.name,
			});
		case EventCategory.InventoryRemoved:
			return t("events.descriptions_with_origin.inventory_removed", {
				origin,
				asset: event.data.inventory_asset.name,
			});
		case EventCategory.WearableAlert:
			return t("events.descriptions_with_origin.wearable_alert", {
				origin,
			});
		case EventCategory.CustomEvent:
			return t("events.descriptions_with_origin.custom_event", {
				origin,
				message: event.data.message,
			});
		default:
			return t("events.descriptions_with_origin.unknown_event", {
				origin,
				category: event.category,
			});
	}
}

/**
 * Maps a connection status to a human-readable string
 * @param status The connection status to map
 */
export function mapConnectionStatus(
	status: ConnectionStatus,
	t: TFunction,
): string {
	switch (status) {
		case "active":
			return t("connection_status.active");
		case "inactive":
			return t("connection_status.inactive");
		case "registered":
			return t("connection_status.registered");
		case "unpaired":
			return t("connection_status.unpaired");
	}
}

/**
 * Maps a floor number to a human-readable string
 */
export function mapFloorNumber(
	floor: number | undefined,
	t: TFunction,
): string {
	if (floor === undefined) return t("floor.all");
	return floor.toString();
}

/**
 * Maps a floor number to a human-readable string,
 * denoted as a floor number
 */
export function mapDescriptiveFloorNumber(floor: number, t: TFunction): string {
	if (floor > 0) {
		return t("floor.above_ground", { floor });
	} else if (floor < 0) {
		return t("floor.below_ground", { floor: -floor });
	} else {
		return t("floor.ground_floor_short");
	}
}

/**
 * Creates a human-readable site location string from a site and an optional floor.
 * If a floor is provided and the site has multiple floors, the floor number
 * is included in the string.
 */
export function getReadableSiteLocation(
	site: VelavuSite,
	floor: VelavuFloor | undefined,
	t: TFunction,
): string {
	if (
		floor !== undefined &&
		site.floors !== undefined &&
		site.floors.length > 1
	) {
		return `${mapDescriptiveFloorNumber(floor.level, t)} • ${site.name}`;
	} else {
		return site.name;
	}
}

/**
 * Maps a floor number to a suggestion of what this floor could be called
 */
export function getDefaultFloorName(floor: number, t: TFunction): string {
	if (floor > 1) {
		return t("floor.floor_n", { floor });
	} else if (floor < 1) {
		return t("floor.basement_n", { floor: -floor });
	} else {
		return t("floor.ground_floor");
	}
}

/**
 * Maps a location type
 * @param type The location type
 */
export function mapLocationType(type: LocationType, t: TFunction): string {
	switch (type) {
		case LocationType.GPS:
			return t("location_type.gps");
		case LocationType.Fixed:
			return t("location_type.fixed");
		case LocationType.Mesh:
			return t("location_type.mesh");
		case LocationType.Proximity:
			return t("location_type.proximity");
		case LocationType.Cellular:
			return t("location_type.cellular");
		case LocationType.WiFi:
			return t("location_type.wifi");
		case LocationType.Internal:
			return t("location_type.internal");
		default:
			return t("location_type.unknown");
	}
}

export const FORMAT_DATE: Intl.DateTimeFormatOptions = {
	month: "short",
	day: "numeric",
};
export const FORMAT_DATE_YEAR: Intl.DateTimeFormatOptions = {
	month: "short",
	day: "numeric",
	year: "numeric",
};
export const FORMAT_TIME: Intl.DateTimeFormatOptions = {
	hour: "numeric",
	minute: "2-digit",
};
export const FORMAT_DATE_TIME: Intl.DateTimeFormatOptions = {
	hour: "numeric",
	minute: "2-digit",
	month: "short",
	day: "numeric",
};
export const FORMAT_DATE_TIME_YEAR: Intl.DateTimeFormatOptions = {
	hour: "numeric",
	minute: "2-digit",
	month: "short",
	day: "numeric",
	year: "numeric",
};
export const FORMAT_DATE_TIME_PRECISE: Intl.DateTimeFormatOptions = {
	hour: "numeric",
	minute: "2-digit",
	second: "2-digit",
	month: "short",
	day: "numeric",
};
export const FORMAT_DATE_TIME_PRECISE_YEAR: Intl.DateTimeFormatOptions = {
	hour: "numeric",
	minute: "2-digit",
	second: "2-digit",
	month: "short",
	day: "numeric",
	year: "numeric",
};

/**
 * Maps an ISO8601 date string to a human-readable string
 * @param date The ISO8601 date string
 * @return The date in dd/mm/yy format
 */
export function formatDate(date: string): string {
	const date_obj: Date = new Date(date);

	return `${date_obj.getDate()}/${date_obj.getMonth() + 1}/${date_obj
		.getFullYear()
		.toString()
		.substr(-2)}`;
}

/**
 * Formats a DateTime to a consistent human-readable date-time string.
 * Output: "3:32 AM, Apr 9" (current year) or "3:32 AM, Apr 9, 2024" (other year).
 */
export function formatDateTime(inputDate: string | DateTime): string {
	//Normalize the input date to a DateTime object
	let dateTime: DateTime;
	if (typeof inputDate === "string") {
		dateTime = DateTime.fromISO(inputDate);
	} else {
		dateTime = inputDate;
	}

	//Format the date time string
	if (dateTime.year === DateTime.now().year) {
		return dateTime.toLocaleString(FORMAT_DATE_TIME);
	} else {
		return dateTime.toLocaleString(FORMAT_DATE_TIME_YEAR);
	}
}

/**
 * Formats a date range to a human-readable format
 */
export function formatDateRange(range: DateRange): string {
	//Convert the date range to Luxon date times
	const start = DateTime.fromJSDate(range.start);
	const end = DateTime.fromJSDate(range.end);

	//Resolve whether to show the year
	const currentYear = DateTime.now().year;
	const isRangeCurrentYear =
		start.year === end.year && start.year === currentYear;

	const isWholeDay =
		start.hour === 0 &&
		start.minute === 0 &&
		end.hour === 23 &&
		end.minute === 59;
	const isSameDay = start.hasSame(end, "day");

	//If both dates are on the same day, we can include the day only at the start
	if (isSameDay) {
		if (isWholeDay) {
			if (isRangeCurrentYear) {
				return start.toLocaleString(FORMAT_DATE);
			} else {
				return start.toLocaleString(FORMAT_DATE_YEAR);
			}
		} else {
			let startFormat: Intl.DateTimeFormatOptions;
			if (isRangeCurrentYear) {
				startFormat = FORMAT_DATE_TIME;
			} else {
				startFormat = FORMAT_DATE_TIME_YEAR;
			}
			return `${start.toLocaleString(startFormat)} → ${end.toLocaleString(FORMAT_TIME)}`;
		}
	} else {
		let format: Intl.DateTimeFormatOptions;
		if (isWholeDay) {
			if (isRangeCurrentYear) {
				format = FORMAT_DATE;
			} else {
				format = FORMAT_DATE_YEAR;
			}
		} else {
			if (isRangeCurrentYear) {
				format = FORMAT_DATE_TIME;
			} else {
				format = FORMAT_DATE_TIME_YEAR;
			}
		}
		return `${start.toLocaleString(format)} → ${end.toLocaleString(format)}`;
	}
}

/**
 * Formats a unit from a statistic to a human-readable string
 */
export function formatUnit(
	stat: AnalyticsStatType,
	value: number,
): string | undefined {
	switch (stat) {
		case AnalyticsStatType.Temperature:
			return Math.round(value) + "º";
		case AnalyticsStatType.Humidity:
		case AnalyticsStatType.Battery:
			return Math.round(value) + "%";
		default:
			return undefined;
	}
}

/**
 * Maps a DFU request status to a human-readable string
 */
export function mapDFURequestStatus(
	status: DFURequestStatus,
	t: TFunction,
): string {
	switch (status) {
		case DFURequestStatus.Pending:
			return t("dfu_status.pending");
		case DFURequestStatus.Started:
			return t("dfu_status.started");
		case DFURequestStatus.Complete:
			return t("dfu_status.complete");
		case DFURequestStatus.Failed:
			return t("dfu_status.failed");
	}
}

/**
 * Maps a normalized device hardware to a human-readable string
 */
export function mapNormalizedDeviceHardware(
	hardware: NormalizedDeviceHardware | undefined,
	t: TFunction,
): string {
	switch (hardware) {
		case NormalizedDeviceHardware.Argo:
			return t("hardware_names.vesta");
		case NormalizedDeviceHardware.Juno:
			return t("hardware_names.arda");
		case NormalizedDeviceHardware.Meridian:
			return t("hardware_names.pavo");
		case NormalizedDeviceHardware.Pisces:
			return t("hardware_names.manta");
		case undefined:
			return t("hardware_names.unknown");
	}
}

/**
 * Ensures a device model is a human-readable string
 */
export function mapDeviceModel(
	deviceModel: string | undefined,
	t: TFunction,
): string {
	//deviceModel is already human-readable
	return deviceModel ?? t("hardware_names.unknown");
}

export function mapDeviceCategory(
	category: DeviceCategory | undefined,
	t: TFunction,
): string {
	switch (category) {
		case DeviceCategory.Tag:
			return t("device_category.tag");
		case DeviceCategory.Anchor:
			return t("device_category.anchor");
		default:
			return category ?? t("device_category.unknown");
	}
}

/**
 * Builds a combined device model and category name, like "Pavo Anchor"
 */
export function mapDeviceModelCategory(
	deviceModel: string | undefined,
	category: DeviceCategory | undefined,
	t: TFunction,
): string {
	return `${mapDeviceModel(deviceModel, t)} ${mapDeviceCategory(category, t)}`;
}

export function mapAssetCategory(
	category: AssetCategory,
	t: TFunction,
): string {
	switch (category) {
		case AssetCategory.Vehicle:
			return t("asset_category.vehicle");
		case AssetCategory.Equipment:
			return t("asset_category.equipment");
		case AssetCategory.Person:
			return t("asset_category.person");
		default:
			return t("asset_category.default");
	}
}

export function mapDeviceSettingsInterval(
	seconds: number,
	t: TFunction,
): string {
	switch (seconds) {
		case 0:
			return t("settings.device_settings.intervals.off");
		case 15:
			return t("settings.device_settings.intervals.15_s");
		case 30:
			return t("settings.device_settings.intervals.30_s");
		case 60:
			return t("settings.device_settings.intervals.1_min");
		case 120:
			return t("settings.device_settings.intervals.2_min");
		case 180:
			return t("settings.device_settings.intervals.3_min");
		case 300:
			return t("settings.device_settings.intervals.5_min");
		case 600:
			return t("settings.device_settings.intervals.10_min");
		case 900:
			return t("settings.device_settings.intervals.15_min");
		case 1800:
			return t("settings.device_settings.intervals.30_min");
		case 3600:
			return t("settings.device_settings.intervals.1_hr");
		case 10800:
			return t("settings.device_settings.intervals.3_hr");
		case 21600:
			return t("settings.device_settings.intervals.6_hr");
		case 43200:
			return t("settings.device_settings.intervals.12_hr");
		case 86400:
			return t("settings.device_settings.intervals.24_hr");
		default:
			return `${seconds} s`;
	}
}

export function mapInvoiceStatusText(
	status: VelavuInvoiceStatus,
	t: TFunction,
): string {
	switch (status) {
		case VelavuInvoiceStatus.Open:
			return t("settings.billing.invoice_status.unpaid");
		case VelavuInvoiceStatus.Paid:
			return t("settings.billing.invoice_status.paid");
		case VelavuInvoiceStatus.Draft:
			return t("settings.billing.invoice_status.draft");
		case VelavuInvoiceStatus.Void:
			return t("settings.billing.invoice_status.void");
		case VelavuInvoiceStatus.Uncollectible:
			return t("settings.billing.invoice_status.uncollectible");
		default:
			return t("settings.billing.invoice_status.unknown");
	}
}

/**
 * Maps a map list display type to a human-readable string
 */
export function mapMapListDisplayType(
	type: MapListDisplayType,
	t: TFunction,
): string {
	switch (type) {
		case MapListDisplayType.Asset:
			return t("map_list.assets");
		case MapListDisplayType.Site:
			return t("map_list.sites");
		case MapListDisplayType.Geofence:
			return t("map_list.geofences");
	}
}

/**
 * Maps a FloorFilter to a human-readable string
 * @param floorFilter
 */
export function mapFloorFilter(floorFilter: FloorFilter, t: TFunction): string {
	switch (floorFilter) {
		case FloorFilter.All:
			return t("floor_filter.all");
		case FloorFilter.Current:
			return t("floor_filter.current");
	}
}

/// Maps an event group to a human-readable string
export function mapEventGroup(eventGroup: EventGroup, t: TFunction): string {
	switch (eventGroup) {
		case EventGroup.All:
			return t("events.groups.all");
		case EventGroup.LowBattery:
			return t("events.groups.low_battery");
		case EventGroup.Connectivity:
			return t("events.groups.connectivity");
		case EventGroup.SitesAndGeofences:
			return t("events.groups.sites_and_geofences");
		case EventGroup.Motion:
			return t("events.groups.motion");
		case EventGroup.Custom:
			return t("events.groups.custom");
	}
}

/**
 * Maps a Velavu rule category to a human-readable string
 */
export function mapRuleCategory(
	category: VelavuRuleCategory,
	t: TFunction,
): string {
	switch (category) {
		case VelavuRuleCategory.System:
			return t("rule_category.system");
		case VelavuRuleCategory.Managed:
			return t("rule_category.managed");
		case VelavuRuleCategory.User:
			return t("rule_category.user");
		default:
			return t("rule_category.unknown");
	}
}

/**
 * Maps a locate method to a human-readable string for a button label
 */
export function mapIndicateMethodButton(
	method: DeviceIndicatorType,
	t: TFunction,
): string {
	switch (method) {
		case DeviceIndicatorType.LED:
			return t("indicate_method.flash_led");
		case DeviceIndicatorType.Buzzer:
			return t("indicate_method.play_sound");
		default:
			return t("indicate_method.locate");
	}
}

/**
 * Maps a {@link ReleaseChannel} to a human-readable string
 */
export function mapReleaseChannel(
	channel: ReleaseChannel,
	t: TFunction,
): string {
	switch (channel) {
		case ReleaseChannel.Stable:
			return t("release_channel.stable");
		case ReleaseChannel.Beta:
			return t("release_channel.beta");
		case ReleaseChannel.Alpha:
			return t("release_channel.alpha");
		default:
			return t("release_channel.unknown");
	}
}
